// apps/web/src/lib/globalErrorHandlers.ts
// ─────────────────────────────────────────────────────────────────────────
// Last-resort window-level handlers for errors that escape React entirely:
//   • unhandledrejection — a promise rejected with no .catch (a fire-and-forget
//     fetch, a floating await). These are invisible by default; here we log
//     them once, classified, so a real failure surfaces instead of vanishing.
//   • error — uncaught synchronous errors and resource load failures.
//
// NOISE FILTERING is the point. Two families of benign messages flood the
// console on this app and are NOT ours to fix:
//   1. Browser-extension content scripts: "A listener indicated an asynchronous
//      response by returning true, but the message channel closed before a
//      response was received." Fires on route changes on virtually every site.
//   2. Firebase popup auth cleanup under COOP: "Cross-Origin-Opener-Policy
//      policy would block the window.close/closed call." Harmless; the auth
//      flow still completes.
// We swallow those so the signal (real rejections) isn't buried. Everything
// else is logged via classifySyncError for a consistent, actionable shape.
//
// Install ONCE from main.tsx before render. Idempotent.
// ─────────────────────────────────────────────────────────────────────────

import { classifySyncError } from '@nisk/core';

// Substrings that identify third-party / platform noise we deliberately ignore.
// Matched case-insensitively against the error message.
const BENIGN_PATTERNS: readonly string[] = [
	'message channel closed before a response was received',
	'a listener indicated an asynchronous response',
	'cross-origin-opener-policy',
	'window.closed',
	'window.close',
	// Firebase's popup resolver occasionally rejects with this when a user
	// dismisses the sign-in popup; it's a user action, not an app error.
	'auth/popup-closed-by-user',
	'auth/cancelled-popup-request',
];

function messageOf(value: unknown): string {
	if (typeof value === 'string') return value;
	if (value instanceof Error) return value.message;
	if (value !== null && typeof value === 'object') {
		const m = (value as { message?: unknown }).message;
		if (typeof m === 'string') return m;
	}
	return '';
}

function isBenign(value: unknown): boolean {
	const msg = messageOf(value).toLowerCase();
	if (!msg) return false;
	return BENIGN_PATTERNS.some((p) => msg.includes(p));
}

let installed = false;

/**
 * Wire the window handlers. Call once, before ReactDOM render. Returns a
 * teardown for tests/HMR; production never needs it.
 */
export function installGlobalErrorHandlers(): () => void {
	if (installed) return () => undefined;
	installed = true;

	const onRejection = (event: PromiseRejectionEvent): void => {
		if (isBenign(event.reason)) {
			// Known third-party/platform noise — stop it here so it doesn't
			// clutter the console or any telemetry we add later.
			event.preventDefault();
			return;
		}

		const classified = classifySyncError(event.reason);
		console.error(
			`[unhandledrejection] ${classified.category}` +
				(classified.traceId ? ` trace=${classified.traceId}` : ''),
			classified.cause,
		);
		// Hook point: POST classified to a telemetry endpoint here in prod.
	};

	const onError = (event: ErrorEvent): void => {
		if (isBenign(event.message) || isBenign(event.error)) {
			event.preventDefault();
			return;
		}
		console.error('[window.error]', event.error ?? event.message);
	};

	window.addEventListener('unhandledrejection', onRejection);
	window.addEventListener('error', onError);

	return () => {
		window.removeEventListener('unhandledrejection', onRejection);
		window.removeEventListener('error', onError);
		installed = false;
	};
}
